import { forwardRef, memo, type HTMLAttributes } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Button } from './button'
import { cn } from '@/lib/utils'
import { placed } from '@/lib/style-guard'

interface EmptyStateAction {
  label: string
  onClick: () => void
  icon?: LucideIcon
}

interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Inside a small card: half the vertical room of the page form. */
  compact?: boolean
  icon: LucideIcon
  title: string
  description?: string
  primaryAction?: EmptyStateAction
  secondaryAction?: EmptyStateAction
  /**
   * Override the derived density. You should almost never pass this — see the
   * header. `quiet` for a region that happens to be empty, `page` for the
   * first-run moment where the user has never created one of these.
   */
  density?: 'quiet' | 'page'
  /**
   * Heading level for `title`. Defaults to 2 — an empty state sitting directly
   * under the page's h1, which is the common case. Measured on `?t=bonger`: the
   * page's only headings were the h1 and this one, and a hardcoded `h3` skipped
   * a level. Pass 3 when this renders inside a section that already has its own
   * h2. Purely semantic: the size comes from `text-lg` either way.
   */
  headingLevel?: 2 | 3
}

/**
 * EmptyState — D-11.
 *
 * ## Empty is a quiet condition, not an event
 *
 * Every one of the 45 call sites used to get the same treatment: a 64px
 * medallion, a centred column, `py-12`, an `h3` and a paragraph. On a tab that
 * simply has no messages yet, that is a container taller than the message
 * inside it, announcing a non-event in the middle of the screen. The chat tab
 * was the extreme case — `h-80 sm:h-[500px]` on the scroller meant a 500px box
 * around one sentence.
 *
 * ## Why there are two densities and not one
 *
 * The directive says «one empty state», and taken literally that is wrong here.
 * «No messages in this tab» and «you have never created an event» are not the
 * same condition: the first is a state to state quietly, the second is the
 * activation moment where a CTA is the entire point. Collapsing both to one
 * grey line would measurably hurt first-run, and «fewer parts» is not worth
 * that.
 *
 * ## The density is DERIVED, not declared
 *
 * A caller does not choose. `primaryAction` present → `page`; absent →
 * `quiet`. The presence of a call to action IS the definition of an activation
 * moment, so the signal already exists in the props and asking for it again
 * would just be a second place to get it wrong. Measured across the codebase:
 * 9 of 45 sites pass a `primaryAction`, and those 9 are exactly the first-run
 * pages (/events, /offers, /catalogue, /presentations, dashboard, and the
 * three admin tabs). The other 36 become quiet with no edit at any call site.
 */
const EmptyStateImpl = forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    { icon: Icon, title, description, primaryAction, secondaryAction, density, headingLevel = 2, compact = false, className, ...props },
    ref,
  ) => {
    const PrimaryIcon = primaryAction?.icon
    const SecondaryIcon = secondaryAction?.icon
    const resolved = density ?? (primaryAction ? 'page' : 'quiet')
    const Heading = (headingLevel === 3 ? 'h3' : 'h2') as 'h2' | 'h3'

    if (resolved === 'quiet') {
      return (
        <div
          ref={ref}
          /* At the content rail, not centred, and no taller than its own text.
             `py-6` is one 4pt step, not a `py-12` void. */
          className={cn('flex items-start gap-2.5 px-4 py-6', placed(className, 'EmptyStateImpl', props))}
          role="status"
          {...props}
        >
          <Icon className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
          <div className="min-w-0">
            {/* One line for what is true, one for what happens next — and the
                second only when there is something to say. Both secondary:
                nothing here is the message, so nothing here is at full
                contrast. */}
            <p className="text-body text-[var(--text-secondary)] leading-snug">{title}</p>
            {description && (
              <p className="mt-0.5 text-small text-[var(--text-muted)] leading-snug">{description}</p>
            )}
            {secondaryAction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={secondaryAction.onClick}
                className="-ml-2 mt-1"
              >
                {SecondaryIcon && <SecondaryIcon className="size-4" />}
                {secondaryAction.label}
              </Button>
            )}
          </div>
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn('flex flex-col items-center text-center px-4', compact ? 'py-6' : 'py-12', placed(className, 'EmptyStateImpl', props))}
        role="status"
        {...props}
      >
        <div
          className="size-16 rounded-full bg-[var(--surface-muted)] border border-[var(--border-default)] flex items-center justify-center mb-5"
          aria-hidden="true"
        >
          <Icon className="size-8 text-[var(--text-muted)]" />
        </div>
        {/* `headingLevel` defaults to 2, which is what an empty state directly
            under the page's h1 needs. Measured on ?t=bonger: the only headings
            on the page were the h1 and this, so a hardcoded h3 skipped a level.
            Callers that nest this inside a section with its own h2 pass 3. The
            SIZE is unchanged either way — `text-lg` is on the element, so the
            level is a semantic choice and not a visual one. */}
        <Heading className="text-lg font-semibold text-[var(--text-primary)] mb-2">{title}</Heading>
        {description && (
          <p className="text-sm text-[var(--text-secondary)] max-w-[280px] mb-6 leading-relaxed">
            {description}
          </p>
        )}
        {(primaryAction || secondaryAction) && (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {primaryAction && (
              <Button onClick={primaryAction.onClick}>
                {PrimaryIcon && <PrimaryIcon className="size-4" />}
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button variant="secondary" onClick={secondaryAction.onClick}>
                {SecondaryIcon && <SecondaryIcon className="size-4" />}
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  },
)
EmptyStateImpl.displayName = 'EmptyState'

export const EmptyState = memo(EmptyStateImpl)
